import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Paper,
  TextField,
  Autocomplete,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { SimCardDownload as ExportIcon, Download as DownloadIcon } from '@mui/icons-material';

interface Option {
  id: string;
  name: string;
}

// See api/DTOs/ReportDataExportDto.cs.
interface Summary {
  totalRows: number;
  records: number;
  fieldSeasons: number;
  excludedRecords: number;
  recordRows: number;
  fieldSeasonPlantingRows: number;
  fieldSeasonPlantingsSkipped: number;
  fieldSeasonValueRows: number;
  rowsByDataSource: Record<string, number>;
}

// Records plus field seasons as an .xlsx in CCA's t_report_data layout —
// see api/Services/ReportData/ReportDataExportService.cs. A field season's
// variety is only exported for a field with no planting record that season;
// Data Validation's "Duplicate or conflicting planting" lists where the two
// disagree.
const ReportDataExportManagement: React.FC = () => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [businesses, setBusinesses] = useState<Option[]>([]);
  const [timePeriods, setTimePeriods] = useState<Option[]>([]);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [timePeriodId, setTimePeriodId] = useState('');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.isSignedIn || !auth.tokens?.accessToken) return;
    (async () => {
      const [businessRes, tpRes] = await Promise.all([
        fetch('/api/businesses/list', { headers: authHeaders() }),
        fetch('/api/time-periods', { headers: authHeaders() }),
      ]);
      if (businessRes.ok) setBusinesses((await businessRes.json()).map((b: any) => ({ id: b.id, name: b.name })));
      if (tpRes.ok) setTimePeriods((await tpRes.json()).map((t: any) => ({ id: t.id, name: t.name })));
    })();
  }, [auth.isSignedIn, auth.tokens?.accessToken, authHeaders]);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (timePeriodId) p.set('timePeriodId', timePeriodId);
    if (businessId) p.set('businessId', businessId);
    return p.toString();
  }, [timePeriodId, businessId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/report-data-export/summary?${params()}`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Failed to load the export summary');
        setSummary(await res.json());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    })();
  }, [params, authHeaders]);

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const res = await fetch(`/api/report-data-export/file?${params()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Export failed');
      const disposition = res.headers.get('Content-Disposition') || '';
      const fileName = /filename="?([^";]+)"?/.exec(disposition)?.[1] || 't_report_data.xlsx';
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setDownloading(false);
    }
  };

  const line = (label: string, value: React.ReactNode, hint?: string) => (
    <TableRow>
      <TableCell sx={{ border: 0, py: 0.5, pl: 0 }}>
        {label}
        {hint && <Typography variant="caption" color="text.secondary" display="block">{hint}</Typography>}
      </TableCell>
      <TableCell align="right" sx={{ border: 0, py: 0.5, fontWeight: 600, verticalAlign: 'top' }}>{value}</TableCell>
    </TableRow>
  );

  return (
    <Box>
      <Typography variant="h6" display="flex" alignItems="center" gap={1} mb={1}>
        <ExportIcon />
        Report Data Export
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Records and field seasons as an .xlsx in CCA&apos;s t_report_data layout (the &quot;RawData&quot; sheet). Field season values go
        out as CCA&apos;s pseudo-product rows — Yield Bales / ha, Irrigation ML/ha, energy and water source, previous crop.
      </Typography>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} mb={2}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="report-export-season-label">Season</InputLabel>
          <Select labelId="report-export-season-label" label="Season" value={timePeriodId} onChange={(e) => setTimePeriodId(e.target.value)}>
            <MenuItem value=""><em>All seasons</em></MenuItem>
            {timePeriods.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Autocomplete
          size="small"
          sx={{ minWidth: 280 }}
          options={businesses}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={businesses.find((b) => b.id === businessId) || null}
          onChange={(_, value) => setBusinessId(value?.id || null)}
          renderInput={(params) => <TextField {...params} label="Business (and its children)" placeholder="All businesses" />}
        />
        <Button
          variant="contained"
          startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          disabled={downloading || loading || !summary || summary.totalRows === 0}
          onClick={handleDownload}
        >
          Download .xlsx
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading && !summary ? (
        <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
      ) : summary && (
        <Paper variant="outlined" sx={{ p: 2, maxWidth: 560, opacity: loading ? 0.6 : 1 }}>
          <Table size="small">
            <TableBody>
              {line('Rows', summary.totalRows.toLocaleString())}
              {line('From records', summary.recordRows.toLocaleString(), `${summary.records.toLocaleString()} records, one row per product line`)}
              {summary.excludedRecords > 0 && line('Records left out', summary.excludedRecords.toLocaleString(), 'Excluded from the export: filter Export on admin Records to see them')}
              {line('Field season values', summary.fieldSeasonValueRows.toLocaleString(), `${summary.fieldSeasons.toLocaleString()} field seasons`)}
              {line('Field season plantings', summary.fieldSeasonPlantingRows.toLocaleString(), 'Variety rows for fields with no planting record')}
              {line('Field season plantings left out', summary.fieldSeasonPlantingsSkipped.toLocaleString(), 'The field has a planting record — see Data Validation for any that disagree')}
            </TableBody>
          </Table>
          {Object.keys(summary.rowsByDataSource).length > 0 && (
            <>
              <Typography variant="subtitle2" mt={2} mb={0.5}>By DataSource</Typography>
              <Table size="small">
                <TableBody>
                  {Object.entries(summary.rowsByDataSource).map(([source, count]) => line(source, count.toLocaleString()))}
                </TableBody>
              </Table>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ReportDataExportManagement;
